import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { RecordData, Service, TeamMember } from 'webapp-types';
import { ModelName, TeamMemberStatus, TeamType } from 'webapp/enums/enums';

import type { TeamSection } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type TeamRecord from 'webapp/models/team';

import type { UserDataForList } from 'webapp/services/model-manager/users';

export default class Team extends BufferedModel<TeamRecord, RecordData.Team.Serialized> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/team-sections')
	declare mmTeamSectionsService: Service.ModelManager.TeamSections;

	// ------- props from / to ED record ------

	get id() {
		return this.model.id;
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get description(): string | null {
		return this.getBuffered('description');
	}

	set description(value: string | null) {
		this.setBuffered('description', value);
	}

	get color(): string {
		return this.getBuffered('color');
	}

	set color(value: string) {
		this.setBuffered('color', value);
	}

	get type(): TeamType {
		return this.getBuffered('type');
	}

	set type(value: TeamType) {
		this.setBuffered('type', value);
	}

	get teamMembers(): TeamMember[] {
		return this.getBuffered('teamMembers');
	}

	set teamMembers(value: TeamMember[]) {
		this.setBuffered('teamMembers', value);
	}

	get ownerId(): number | null {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number | null) {
		this.setBuffered('ownerId', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	@cached
	get orderedSections(): TeamSection[] {
		return this.mmTeamSectionsService.sectionModels
			.filter((section) => section.teamId === this.numericId)
			.sort((a, b) => a.position - b.position);
	}

	@cached
	get stringifiedOwnerId(): string {
		return this.ownerId ? this.ownerId.toString() : '';
	}

	@cached
	get activeTeamMembers(): FilterOption<string>[] {
		const members: FilterOption<string>[] = [];

		this.teamMembers.forEach((member) => {
			this.mmUsersService.activatedUserModels.forEach((user) => {
				if (user.numericId === member.id) {
					members.push({ id: member.id.toString(), text: user.name });
				}
			});
		});

		return members;
	}

	get isOwnedByMe(): boolean {
		return this.ownerId === this.currentUserService.numericId;
	}

	get isUserMember(): boolean {
		return this.teamMembers.some(
			(member) =>
				member.id === this.currentUserService.numericId &&
				member.status === TeamMemberStatus.MEMBER,
		);
	}

	get isPrivate(): boolean {
		return this.type === TeamType.PRIVATE;
	}

	get isClosed(): boolean {
		return this.type === TeamType.CLOSED;
	}

	get isOpen(): boolean {
		return this.type === TeamType.OPEN;
	}

	get hasUserSentRequest(): boolean {
		return this.teamMembers.some(
			(member) =>
				member.id === this.currentUserService.numericId &&
				member.status === TeamMemberStatus.REQUEST,
		);
	}

	@cached
	get usersList(): UserDataForList[] {
		return this.mmUsersService.activatedAndVerifiedUsersWithoutViewersList;
	}

	@cached
	get normalizedUsers() {
		const ownerId = this.stringifiedOwnerId;

		return this.usersList.map((user: UserDataForList) => {
			return {
				value: user.value,
				label: user.label,
				label2: user.label2,
				disabled: user.value === ownerId,
			};
		});
	}

	get teamTypeForTracking(): string {
		const teamType = this.model.type;
		if (teamType === TeamType.OPEN) {
			return 'open';
		} else if (teamType === TeamType.CLOSED) {
			return 'closed';
		}

		return 'private';
	}

	get isCurrentUserMember() {
		return this.teamMembers.some(
			(member) => member.id === this.currentUserService.model?.numericId,
		);
	}

	//after v1
	/*
	get order(): number | null {
		return this.getBuffered('order');
	}

	set order(value: number | null) {
		this.setBuffered('order', value);
	}
 */
	// ------- other props ------

	static override modelName = ModelName.TEAM; // store record type
}
